import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'
// Relative imports (not the '@/components' barrel) to avoid a cycle now that
// the barrel exports Workbench, which renders this file.
import { Dialog, DialogContent, DialogTitle } from '../Dialog/Dialog'
import { Button } from '../Button/Button'
import { Checkbox } from '../Checkbox/Checkbox'
import { SwatchGrid } from './WorkbenchColorPicker'
import { BitsPopover } from './WorkbenchFloatingBits'
import { useIsDark } from './useIsDark'
import { defaultBackground } from './chartStyles'
import type { ChartSettings } from './types'

// TradingView-shaped chart settings. Uppercase section labels, one row per
// feature, a real checkbox that toggles the element on/off (off = transparent
// on the chart: hollow body / no border / no wick), and the bull + bear colours
// sit side by side. Unset keys fall back to the theme default (mirrors
// buildStyles in chartStyles.ts). Every edit calls onChange immediately so the
// chart live-previews behind the (transparent-overlay) dialog.

export interface WorkbenchSettingsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  settings: ChartSettings
  onChange: (patch: Partial<ChartSettings>) => void
  onReset: () => void
  /** Replay the first-run onboarding tour; the row is hidden when absent. */
  onResetOnboarding?: () => void
}

export function WorkbenchSettingsDialog({
  open,
  onOpenChange,
  settings,
  onChange,
  onReset,
  onResetOnboarding,
}: WorkbenchSettingsDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        overlayClassName="bg-transparent"
        className="v1 bg-card max-w-[400px] sm:max-w-[400px] w-[94vw] p-0 gap-0 overflow-visible"
      >
        <DialogTitle className="px-5 pt-5 pb-2 text-section text-foreground">
          Chart settings
        </DialogTitle>
        {/* Inner component: tab state resets naturally each open (Radix unmounts). */}
        <SettingsBody
          settings={settings}
          onChange={onChange}
          onReset={onReset}
          onResetOnboarding={onResetOnboarding}
          onClose={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  )
}

function SettingsBody({
  settings: s,
  onChange,
  onReset,
  onResetOnboarding,
  onClose,
}: {
  settings: ChartSettings
  onChange: (patch: Partial<ChartSettings>) => void
  onReset: () => void
  onResetOnboarding?: () => void
  onClose: () => void
}) {
  const [tab, setTab] = useState<'symbol' | 'canvas'>('symbol')
  const isDark = useIsDark()

  // Theme defaults (mirror buildStyles in chartStyles.ts).
  const bull = isDark ? 'rgba(38,166,154,0.85)' : 'rgba(22,163,74,0.85)'
  const bear = isDark ? 'rgba(242,54,69,0.85)' : 'rgba(220,38,38,0.85)'
  const bullWick = isDark ? 'rgba(38,166,154,0.6)' : 'rgba(22,163,74,0.6)'
  const bearWick = isDark ? 'rgba(242,54,69,0.6)' : 'rgba(220,38,38,0.6)'
  const grid = isDark ? '#2e2e2e' : 'rgba(0,0,0,0.06)'
  const text = isDark ? '#888888' : '#737373'
  const scale = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'

  return (
    <>
      <div className="px-5 pt-1">
        {/* Segmented control (the app has no Tabs primitive). */}
        <div className="flex w-full items-center gap-0.5 rounded-md bg-muted/40 p-0.5">
          {(['symbol', 'canvas'] as const).map((id) => (
            <button
              key={id}
              type="button"
              onClick={() => setTab(id)}
              aria-pressed={tab === id}
              className={cn(
                'flex-1 h-7 rounded-[5px] text-label transition-colors cursor-pointer',
                tab === id ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {id === 'symbol' ? 'Symbol' : 'Canvas'}
            </button>
          ))}
        </div>

        {tab === 'symbol' ? (
          <Section label="Candles">
            <ToggleRow
              label="Body"
              on={s.bodyOn !== false}
              onToggle={(v) => onChange({ bodyOn: v })}
              up={s.candleUp}
              down={s.candleDown}
              upFallback={bull}
              downFallback={bear}
              onUp={(c) => onChange({ candleUp: c })}
              onDown={(c) => onChange({ candleDown: c })}
            />
            <ToggleRow
              label="Borders"
              on={s.bordersOn !== false}
              onToggle={(v) => onChange({ bordersOn: v })}
              up={s.candleUpBorder}
              down={s.candleDownBorder}
              upFallback={s.candleUp || bull}
              downFallback={s.candleDown || bear}
              onUp={(c) => onChange({ candleUpBorder: c })}
              onDown={(c) => onChange({ candleDownBorder: c })}
            />
            <ToggleRow
              label="Wick"
              on={s.wickOn !== false}
              onToggle={(v) => onChange({ wickOn: v })}
              up={s.candleUpWick}
              down={s.candleDownWick}
              upFallback={bullWick}
              downFallback={bearWick}
              onUp={(c) => onChange({ candleUpWick: c })}
              onDown={(c) => onChange({ candleDownWick: c })}
            />
          </Section>
        ) : (
          <>
            <Section label="Chart basic styles">
              <Row label="Background">
                <Swatch
                  value={s.background}
                  fallback={defaultBackground(isDark)}
                  onPick={(c) => onChange({ background: c })}
                />
              </Row>
              <Row label="Grid lines">
                <Swatch value={s.gridVert} fallback={grid} onPick={(c) => onChange({ gridVert: c })} />
                <Swatch value={s.gridHoriz} fallback={grid} onPick={(c) => onChange({ gridHoriz: c })} />
              </Row>
              <Row label="Crosshair">
                <Swatch value={s.crosshair} fallback={text} onPick={(c) => onChange({ crosshair: c })} />
              </Row>
            </Section>
            <Section label="Scales">
              <Row label="Text">
                <Swatch value={s.text} fallback={text} onPick={(c) => onChange({ text: c })} />
                <SizeSelect value={s.textSize ?? 12} onChange={(n) => onChange({ textSize: n })} />
              </Row>
              <Row label="Lines">
                <Swatch value={s.scale} fallback={scale} onPick={(c) => onChange({ scale: c })} />
              </Row>
            </Section>
          </>
        )}
      </div>

      <div className="flex items-center justify-between gap-2.5 px-5 py-3 mt-3 border-t border-border">
        {onResetOnboarding ? (
          <Button type="button" variant="ghost" size="sm" onClick={onResetOnboarding}>
            Reset onboarding
          </Button>
        ) : (
          <div />
        )}
        <div className="flex items-center gap-2.5">
          <Button type="button" variant="ghost" size="sm" onClick={onReset}>
            Reset to default
          </Button>
          <Button type="button" size="sm" onClick={onClose}>
            Done
          </Button>
        </div>
      </div>
    </>
  )
}

function Section({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div className="text-eyebrow text-muted-foreground mt-4 mb-2.5">{label}</div>
      <div className="flex flex-col gap-3">{children}</div>
    </div>
  )
}

// Symbol row: checkbox + label on the left, bull/bear swatches side by side right.
function ToggleRow({
  label,
  on,
  onToggle,
  up,
  down,
  upFallback,
  downFallback,
  onUp,
  onDown,
}: {
  label: string
  on: boolean
  onToggle: (on: boolean) => void
  up?: string
  down?: string
  upFallback: string
  downFallback: string
  onUp: (color: string) => void
  onDown: (color: string) => void
}) {
  return (
    <div className="flex items-center justify-between">
      <label className="flex items-center gap-2.5 cursor-pointer">
        <Checkbox checked={on} onCheckedChange={(v) => onToggle(v === true)} />
        <span className="text-body text-foreground">{label}</span>
      </label>
      <div className="flex items-center gap-2">
        <Swatch value={up} fallback={upFallback} onPick={onUp} />
        <Swatch value={down} fallback={downFallback} onPick={onDown} />
      </div>
    </div>
  )
}

// Canvas row: label left, one-or-more controls right.
function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-body text-foreground">{label}</span>
      <div className="flex items-center gap-2">{children}</div>
    </div>
  )
}

function Swatch({
  value,
  fallback,
  onPick,
}: {
  value?: string
  fallback: string
  onPick: (color: string) => void
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (!open) return
    const onDown = (e: PointerEvent) => {
      if (ref.current && e.target instanceof Node && !ref.current.contains(e.target)) {
        setOpen(false)
      }
    }
    document.addEventListener('pointerdown', onDown)
    return () => document.removeEventListener('pointerdown', onDown)
  }, [open])
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Pick colour"
        className="h-7 w-7 rounded-md border border-border cursor-pointer"
        style={{ background: value || fallback }}
      />
      {open && (
        <BitsPopover align="right">
          <SwatchGrid
            cur={value}
            onPick={(c) => {
              onPick(c)
              setOpen(false)
            }}
          />
        </BitsPopover>
      )}
    </div>
  )
}

function SizeSelect({ value, onChange }: { value: number; onChange: (n: number) => void }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="h-7 rounded-md border border-border bg-muted px-2 text-caption text-foreground outline-none cursor-pointer"
    >
      {[9, 10, 11, 12, 13, 14, 16, 18].map((n) => (
        <option key={n} value={n}>
          {n}
        </option>
      ))}
    </select>
  )
}
